import { SetStateAction, useEffect, useMemo, useState } from "react";
import { Stack, IconButton } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { addCommentApi, ChatAppResponse, getCitationFilePath, SpeechConfig, addChatHistoryPostgresqlApi, getDevUsersFromPostgresqlApi } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";
import { getUsername } from "../../authConfig";

interface Props {
    answer: ChatAppResponse;
    index: number;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
    question: string;
    retrieveCount: number;
    retrievalMode: string;
    isLastAnswer: boolean;
    model: string;
    categories: string[];
}

export const Answer = ({
    answer,
    index,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser,
    question,
    retrieveCount,
    retrievalMode,
    isLastAnswer,
    model,
    categories
}: Props) => {
    const followupQuestions = answer.context?.followup_questions;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming, onCitationClicked), [answer]);
    const { t } = useTranslation();
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    const [copied, setCopied] = useState(false);
    const [comment, setComment] = useState("");
    const [rating, setRating] = useState<number>(0);

    function showCustomAlert(message: string) {
        // Create a custom alert div
        const alertDiv = document.createElement("div");
        alertDiv.style.position = "fixed";
        alertDiv.style.top = "20px";
        alertDiv.style.left = "50%";
        alertDiv.style.transform = "translateX(-50%)";
        alertDiv.style.padding = "10px 20px";
        alertDiv.style.backgroundColor = "#4CAF50"; // Green background
        alertDiv.style.color = "white";
        alertDiv.style.borderRadius = "5px";
        alertDiv.style.fontSize = "16px";
        alertDiv.style.zIndex = "1000";
        alertDiv.innerText = message;

        // Append the custom alert div to the body
        document.body.appendChild(alertDiv);

        // Remove the custom alert after 2 seconds
        setTimeout(() => {
            alertDiv.remove();
        }, 2000); // 2000 milliseconds = 2 seconds
    }

    const handleCopy = () => {
        // Single replace to remove all HTML tags to remove the citations
        const textToCopy = sanitizedAnswerHtml.replace(/<a [^>]*><sup>\d+<\/sup><\/a>|<[^>]+>/g, "");

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };
    const [username, setUsername] = useState("");
    const { instance } = useMsal();

    useEffect(() => {
        const fetchUsername = async () => {
            setUsername((await getUsername(instance)) ?? "");
        };

        fetchUsername();
    }, []);

    const [devUsers, setDevUsers] = useState<string[]>([]); // Initialize as an empty array

    useEffect(() => {
        const fetchDevUsers = async () => {
            try {
                const users = await getDevUsersFromPostgresqlApi();
                setDevUsers(users);
            } catch (error) {
                console.error("Error fetching allowed users:", error);
                // Handle the error appropriately, e.g., display an error message to the user
                // or set a default list of allowed users.
                setDevUsers(["xxx.xx@vinci-energies.net"]); // Default user in case of error
            }
        };

        fetchDevUsers();
    }, []);

    useEffect(() => {
        const saveChatHistory = async () => {
            if (!isStreaming && sanitizedAnswerHtml && username && !answer.isHistorySaved) {
                await addChatHistoryPostgresqlApi(username, question, sanitizedAnswerHtml, retrieveCount, retrievalMode, model, categories);
                answer.isHistorySaved = true;
            }
        };

        saveChatHistory();
    }, [isStreaming, sanitizedAnswerHtml, username, answer, question, retrieveCount, retrievalMode, model, categories]);

    const handleSubmit = async () => {
        if (!rating) {
            alert("Merci de choisir une note avant de valider.");
            return;
        }

        const response = await addCommentApi(
            username,
            question, // Ensure 'answer' contains the question field
            sanitizedAnswerHtml, // Use sanitized answer
            comment,
            rating,
            retrieveCount,
            retrievalMode,
            categories
        );

        if (response.ok) {
            showCustomAlert("Le commentaire est correctement enregistré. Merci beaucoup de votre retour!");
            setComment("");
            setRating(0);
            setIsCommentSubmitted(true); // Masquer la section après soumission
        } else {
            const data = await response.json();
            alert("Erreur : " + data.error + username + question + sanitizedAnswerHtml + comment + rating);
        }
    };

    const handleRatingChange = (star: SetStateAction<number>) => {
        setRating(star);
    };

    const [hover, setHover] = useState<number>(0);

    const [isCommentSubmitted, setIsCommentSubmitted] = useState(false);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: copied ? "CheckMark" : "Copy" }}
                            title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            ariaLabel={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            onClick={handleCopy}
                        />
                        {/* Afficher ou pas le processus de réflexion */}
                        {devUsers.includes(username) && (
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "Lightbulb" }}
                                title={t("tooltips.showThoughtProcess")}
                                ariaLabel={t("tooltips.showThoughtProcess")}
                                onClick={() => onThoughtProcessClicked()}
                                disabled={!answer.context.thoughts?.length}
                            />
                        )}
                        {/* Afficher ou pas les contenus de soutien */}
                        {devUsers.includes(username) && (
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "ClipboardList" }}
                                title={t("tooltips.showSupportingContent")}
                                ariaLabel={t("tooltips.showSupportingContent")}
                                onClick={() => onSupportingContentClicked()}
                                disabled={!answer.context.data_points}
                            />
                        )}
                        {showSpeechOutputAzure && (
                            <SpeechOutputAzure answer={sanitizedAnswerHtml} index={index} speechConfig={speechConfig} isStreaming={isStreaming} />
                        )}
                        {showSpeechOutputBrowser && <SpeechOutputBrowser answer={sanitizedAnswerHtml} />}
                    </div>
                </Stack>
            </Stack.Item>
            <Stack.Item grow>
                <div className={styles.answerText}>
                    <ReactMarkdown children={sanitizedAnswerHtml} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} />
                </div>
            </Stack.Item>
            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>{t("citationWithColon")}</span>
                        {parsedAnswer.citations.map((x, i) => {
                            const path = getCitationFilePath(x);
                            return (
                                <a key={i} className={styles.citation} title={x} onClick={() => onCitationClicked(path)}>
                                    {`${++i}. ${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && !isStreaming && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>{t("followupQuestions")}</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
            {isLastAnswer && !isStreaming && !isCommentSubmitted && (
                <Stack.Item>
                    <div className={styles.commentSection}>
                        <label className={styles.commentLabel}>
                            Votre commentaire sur la réponse de l'IA : (pour aider à améliorer les réponses futures, si vous connaissez la bonne réponse, merci
                            de la détailler ici)
                        </label>
                        <textarea
                            className={styles.commentInput}
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            placeholder="Écrivez votre commentaire ..."
                        />

                        <div className={styles.starRating}>
                            {[5, 4, 3, 2, 1].map(star => (
                                <label key={star} style={{ cursor: "pointer" }}>
                                    <input
                                        type="radio"
                                        name="rating"
                                        value={star}
                                        checked={rating === star}
                                        onChange={() => handleRatingChange(star)}
                                        style={{ display: "none" }}
                                    />
                                    <span
                                        className={hover >= star || rating >= star ? styles.filledStar : styles.emptyStar}
                                        onMouseEnter={() => setHover(star)}
                                        onMouseLeave={() => setHover(0)}
                                    >
                                        ★
                                    </span>
                                </label>
                            ))}
                        </div>

                        <button className={styles.submitButton} onClick={handleSubmit}>
                            Valider le commentaire
                        </button>
                    </div>
                </Stack.Item>
            )}
        </Stack>
    );
};
